import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Typography, List, ListItem, ListItemText, useTheme } from '@mui/material';
import { dispatch, useSelector } from 'src/redux/store';
import { setOpenCallScriptFromLeadPage } from 'src/redux/slices/callScript';
import { callApi } from 'src/utils/apiCall';
import Iconify from '../Iconify';
import { NormalLoading } from '../loading';
import SingleScriptPreview from './SingleScriptPreview';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';
import { callScriptListItemStyle, closeButtonStyle } from '../../styles/CallScripts';

const mapSubmissionFields = (fields) => fields?.map(submission => {
    if (submission?.field?.field_type?.field_type === "choice") {
        if (submission?.field?.field_type?.attribute === "lead_source_type") {
            return ({
                staticName: "leadType", name: submission?.field?.label, value: submission?.value, type: submission?.field?.field_type?.field_type, id: submission?.field?.id
            })
        }
        if (submission?.field?.field_type?.ui_type === 'DROPDOWN') {
            return ({
                name: submission?.field?.label, value: submission?.value, type: "checkbox", id: submission?.field?.id
            })
        }
        if (submission?.field?.field_type?.ui_type === 'MULTI_SELECT') {
            return ({
                name: submission?.field?.label, value: submission?.value, type: "multiCheckbox", id: submission?.field?.id
            })
        }
        if (submission?.field?.field_type?.ui_type === 'CHECKBOX') {
            if (submission?.field?.field_type?.attribute === 'lead_is_registered_patient') {
                return ({
                    staticName: "isRegister", name: submission?.field?.label, value: submission?.value, type: submission?.field?.field_type?.field_type, id: submission?.field?.id
                })
            }
            return ({
                name: submission?.field?.label, value: submission?.value, type: "typeCheckbox", id: submission?.field?.id
            })
        }
        if (submission?.field?.field_type?.ui_type === 'RADIO') {
            if (submission?.field?.field_type?.attribute === 'lead_preferred_contact_method') {
                return ({
                    staticName: "preferredContactMethod", name: submission?.field?.label, value: submission?.value, type: submission?.field?.field_type?.field_type, id: submission?.field?.id
                })
            }
            return ({
                name: submission?.field?.label, value: submission?.value, type: "typeRadio", id: submission?.field?.id
            })
        }
    }
    return ({
        name: submission?.field?.label, value: submission?.value, type: submission?.field?.field_type?.field_type, id: submission?.field?.id
    })
});

const CallScriptListing = ({ type, scriptList, setCallScriptActive, onClose, followLead, onOpenAppointmentNotBooked, setLeadId, onOpenAppointmentBooked, setTreatmentId }) => {
    const [singleScriptActive, setSingleScriptActive] = useState(scriptList?.length === 1);
    const theme = useTheme();
    const isScriptTCO = type === "TCO";
    const isLight = theme.palette.mode === 'light';
    const [scriptData, setScriptData] = useState(scriptList?.length === 1 ? scriptList[0] : []);
    const [previousData, setPreviousData] = useState(null);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // Tracks the latest in-flight request so a stale response can't overwrite newer state
    // (e.g. user picks script B before script A's fetch resolves).
    const latestRequestIdRef = useRef(0);

    const BackButtonClick = () => {
        if (scriptList?.length === 1) {
            setCallScriptActive(false);
            return;
        }
        setSingleScriptActive(!singleScriptActive);
    };

    // Match the script against the lead's existing form submissions (slim payload still
    // exposes form.{name, category, form_type}), then lazy-fetch the heavy fields[] from
    // the dedicated endpoint and feed them through the existing field mapper.
    const loadPastSubmissionData = async (updateValue) => {
        const requestId = latestRequestIdRef.current + 1;
        latestRequestIdRef.current = requestId;

        const formName = updateValue?.name;
        const formType = updateValue?.form_type;
        const formCategory = updateValue?.category;
        const pastSubmissions = followLead?.form_submissions ?? [];
        const matched = pastSubmissions?.find(
            (pastForms) =>
                pastForms?.form?.name === formName &&
                pastForms?.form?.category === formCategory &&
                pastForms?.form?.form_type === formType
        );

        if (!matched) {
            // No past submission for this script — preserve existing UX by clearing.
            if (latestRequestIdRef.current === requestId) {
                setPreviousData(null);
            }
            return;
        }

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${followLead?.id}/form-submissions/?form=${matched?.form?.id}&ordering=-submitted_at&limit=1`
            );
            // Discard if a newer request superseded this one.
            if (latestRequestIdRef.current !== requestId) return;

            const fullSubmission = response?.data?.results?.[0];
            const pastFormSubmissionFormDetails = fullSubmission?.fields;
            const fieldNameToValueMapping = mapSubmissionFields(pastFormSubmissionFormDetails);
            setPreviousData(
                fieldNameToValueMapping
                    ? { data: fieldNameToValueMapping, time: matched?.updated_at_human }
                    : null
            );
        } catch (error) {
            // Preserve existing UX (no error toast). Log for diagnostics only.
            // eslint-disable-next-line no-console
            console.error('Failed to load past submission fields', error);
            if (latestRequestIdRef.current === requestId) {
                setPreviousData(null);
            }
        }
    };

    useEffect(() => {
        if (singleScriptActive) {
            loadPastSubmissionData(scriptList[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleScriptClick = (value) => {
        const updateValue = { ...value, sections: [...value?.sections]?.sort((a, b) => a.order - b.order) };
        setSingleScriptActive(!singleScriptActive);
        setScriptData(updateValue);
        loadPastSubmissionData(updateValue);
    };

    return (
        <Box sx={{ position: 'relative' }}>
            {!singleScriptActive ? <Card sx={{ width: isScriptTCO ? '100%' : '563px', mx: 'auto' }}>
                <Box sx={{ p: '15px 16px', borderBottom: 'solid 1px rgba(145, 158, 171, 0.24)', position: 'relative' }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>{isScriptTCO ? "TCO Scripts" : "Call Scripts"}</Typography>
                    {<Box sx={closeButtonStyle} onClick={() => { setCallScriptActive(false); dispatch(setOpenCallScriptFromLeadPage(false)) }}>
                        <Iconify icon={'eva:close-fill'} width={'20px'} height={'20px'} />
                    </Box>}
                </Box>
                <Box sx={{ p: '24px' }}>
                    <Box>
                        <NormalLoading tableData={scriptList === undefined && 'No Found'} pageCount={scriptList === undefined && 'No Found'} />
                        <List sx={{ m: '0', p: '0' }}>
                            {scriptList?.map((value, index) => (
                                <ListItem key={index} sx={callScriptListItemStyle(isLight)} onClick={() => handleScriptClick(value)}>
                                    <ListItemText primary={`${capitalizeFirstLetter(value.name)}`} sx={{ '.MuiTypography-root': { fontSize: '13px', fontWeight: '700' } }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Iconify icon={'akar-icons:chevron-right'} width={'20px'} height={'20px'} />
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Card> :
                <SingleScriptPreview isScriptTCO={isScriptTCO} scriptData={scriptData} BackButtonClick={BackButtonClick} onClose={onClose} followLead={followLead} onOpenAppointmentNotBooked={onOpenAppointmentNotBooked} setLeadId={setLeadId} onOpenAppointmentBooked={onOpenAppointmentBooked} setTreatmentId={setTreatmentId} previousData={previousData} />
            }
        </Box>
    );
};

export default CallScriptListing;

CallScriptListing.propTypes = {
    scriptList: PropTypes.array,
    setCallScriptActive: PropTypes.func,
    onClose: PropTypes.func,
    followLead: PropTypes.any,
    onOpenAppointmentNotBooked: PropTypes.func,
    setLeadId: PropTypes.func,
    onOpenAppointmentBooked: PropTypes.func,
    setTreatmentId: PropTypes.func,
    type: PropTypes.string
};